import React from 'react';
import { Input, InputField } from '@gluestack-ui/themed';
import {
  GraduationCap, PenLine, Plus, RefreshCcw, Reply, ReplyAll, Trash,
} from 'lucide-react-native';
import i18next from 'i18next';
import {
  deleteModuleSubmission,
  grantExamAttempt,
  overrideModuleGrade,
  returnModuleSubmission,
  upgradeSubmissionToOralExam,
} from '../../../common/api/endpoints/submissions/submissions';
import type { ActionConfig, ActionHandlerProps } from '../DataTable/actionTypes';
import log from '../../../common/utils/logger';
import { ModuleSubmissionState, SubmissionRecord } from '../../../common/api/models';
import {
  GradeOverrideDialogBody,
  isGradeOverrideFormValid,
  type GradeOverrideMode,
} from '../../Panels/Forms/User/GradeOverrideDialog';

const submittedOrGraded = (data: unknown) => {
  const item = data as SubmissionRecord;
  return item?.state === ModuleSubmissionState.submitted
    || item?.state === ModuleSubmissionState.graded;
};

function requestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `grant-${Date.now()}`;
}

type GradeOverrideDialogContentProps = {
  mode: GradeOverrideMode;
  attemptNumber?: number | null;
  inlineError: string | null;
  initialGrade?: string;
  initialComment?: string;
  onValidityChange: (valid: boolean) => void;
  onPayloadChange: (payload: { grade: string; comment: string }) => void;
};

function GradeOverrideDialogContent({
  mode,
  attemptNumber,
  inlineError,
  initialGrade = '',
  initialComment = '',
  onValidityChange,
  onPayloadChange,
}: GradeOverrideDialogContentProps) {
  const [grade, setGrade] = React.useState(initialGrade);
  const [comment, setComment] = React.useState(initialComment);

  React.useEffect(() => {
    setGrade(initialGrade);
  }, [initialGrade]);

  React.useEffect(() => {
    setComment(initialComment);
  }, [initialComment]);

  React.useEffect(() => {
    onPayloadChange({ grade, comment });
    onValidityChange(isGradeOverrideFormValid(grade, comment));
  }, [grade, comment, onPayloadChange, onValidityChange]);

  return (
    <GradeOverrideDialogBody
      mode={mode}
      attemptNumber={attemptNumber}
      inlineError={inlineError}
      initialGrade={grade}
      initialComment={comment}
      onGradeChange={setGrade}
      onCommentChange={setComment}
    />
  );
}

function GrantNoteField({ onNote }: { onNote: (value: string) => void }) {
  const [note, setNote] = React.useState('');
  return (
    <Input mt="$3">
      <InputField
        placeholder="Why is this extra attempt granted?"
        value={note}
        onChangeText={(value) => {
          onNote(value);
          setNote(value);
        }}
      />
    </Input>
  );
}

const adminOverrideGraded = (data: unknown) => {
  const item = data as SubmissionRecord;
  return item?.state === ModuleSubmissionState.graded
    && !!item?.grading?.adminOverride?.comment;
};

const openGradeOverrideDialog = (
  mode: GradeOverrideMode,
  { data, utils, objectName }: ActionHandlerProps,
) => {
  const {
    showDialog, showToastMessage, setRefreshKey, queryClient, hideDialog, updateDialogProps,
  } = utils;
  const item = data as SubmissionRecord;
  const attemptNumber = mode === 'overwrite' ? item?.attemptNumber ?? null : null;
  const payloadRef = { grade: '', comment: '' };
  const validRef = { current: false };
  const errorRef = { current: null as string | null };

  const primaryLabel = mode === 'set'
    ? i18next.t('admin.gradeOverride.setAction')
    : i18next.t('admin.gradeOverride.overwriteAction');
  const title = mode === 'set'
    ? i18next.t('admin.gradeOverride.setTitle')
    : i18next.t('admin.gradeOverride.overwriteTitle');

  const refreshTable = () => {
    if (queryClient) {
      queryClient.invalidateQueries({ queryKey: [`/${objectName}`] });
    }
    setRefreshKey(Date.now());
  };

  const saveRef: { current: () => Promise<void> } = {
    current: async () => {},
  };

  const dialogButtons = () => [
    {
      text: i18next.t('admin.gradeOverride.cancelAction'),
      variant: 'outline',
      action: 'secondary' as const,
    },
    {
      text: primaryLabel,
      action: 'primary' as const,
      preventAutoClose: true,
      isDisabled: !validRef.current,
      onPress: () => saveRef.current(),
    },
  ];

  const syncPrimaryDisabled = () => {
    updateDialogProps({ buttons: dialogButtons() });
  };

  const onValidityChange = (valid: boolean) => {
    validRef.current = valid;
    syncPrimaryDisabled();
  };

  const saveGrade = async () => {
    if (!validRef.current || !isGradeOverrideFormValid(payloadRef.grade, payloadRef.comment)) {
      return;
    }
    try {
      await overrideModuleGrade(
        item?.student?.id || '',
        item?.course?.id || '',
        item?.module?.id || '',
        {
          grade: Number(payloadRef.grade),
          comment: payloadRef.comment.trim(),
        },
        item?.etag ? { 'If-Match': item.etag } : undefined,
      );
      hideDialog();
      showToastMessage('success', primaryLabel);
      refreshTable();
    } catch (error: unknown) {
      const apiError = (error as { response?: { data?: { error?: { code?: string } } } })
        ?.response?.data?.error;
      if (apiError?.code === 'SUBMISSION_MODIFIED_CONCURRENTLY'
        || apiError?.code === 'GRADE_OVERRIDE_NOT_ALLOWED') {
        errorRef.current = i18next.t('admin.gradeOverride.conflictError');
        refreshTable();
      } else {
        errorRef.current = i18next.t('admin.gradeOverride.saveError');
      }
      updateDialogProps({
        children: (
          <GradeOverrideDialogContent
            mode={mode}
            attemptNumber={attemptNumber}
            inlineError={errorRef.current}
            initialGrade={payloadRef.grade}
            initialComment={payloadRef.comment}
            onPayloadChange={(payload) => {
              payloadRef.grade = payload.grade;
              payloadRef.comment = payload.comment;
            }}
            onValidityChange={onValidityChange}
          />
        ),
        buttons: dialogButtons(),
      });
      log.error('Grade override error:', error);
    }
  };
  saveRef.current = saveGrade;

  showDialog({
    title,
    message: '',
    children: (
      <GradeOverrideDialogContent
        mode={mode}
        attemptNumber={attemptNumber}
        inlineError={errorRef.current}
        initialGrade={payloadRef.grade}
        initialComment={payloadRef.comment}
        onPayloadChange={(payload) => {
          payloadRef.grade = payload.grade;
          payloadRef.comment = payload.comment;
        }}
        onValidityChange={onValidityChange}
      />
    ),
    buttons: dialogButtons(),
  });
};

export const submissionActions: Record<string, Partial<ActionConfig>> = {
  setAdminGrade: {
    id: 'setAdminGrade',
    label: i18next.t('admin.gradeOverride.setRowAction'),
    icon: PenLine,
    position: 'row',
    rowStyle: 'menu',
    availCondition: (data) => (data as SubmissionRecord)?.state === ModuleSubmissionState.draft,
    handler: async (props) => {
      openGradeOverrideDialog('set', props);
    },
  },
  overwriteAdminGrade: {
    id: 'overwriteAdminGrade',
    label: i18next.t('admin.gradeOverride.overwriteRowAction'),
    icon: RefreshCcw,
    position: 'row',
    rowStyle: 'menu',
    availCondition: adminOverrideGraded,
    handler: async (props) => {
      openGradeOverrideDialog('overwrite', props);
    },
  },
  returnSubmissionToStudent: {
    id: 'returnSubmissionToStudent',
    label: 'Return to student for rework (keep their work)',
    icon: ReplyAll,
    position: 'row',
    rowStyle: 'menu',
    availCondition: submittedOrGraded,
    handler: async ({ data, utils, objectName }: ActionHandlerProps) => {
      const {
        showDialog, showToastMessage, setRefreshKey, queryClient,
      } = utils;
      const item = data as SubmissionRecord;

      showDialog({
        title: 'Return Submission to Student',
        message: 'This keeps the same exam attempt. The student can correct their work. Oral exams and regular tasks that are not AI-tutored are reset. AI Tutor work stays finished. It does not add an exam attempt.',
        buttons: [
          { text: 'Cancel', variant: 'outline', action: 'secondary' },
          {
            text: 'Return to Student',
            action: 'primary',
            onPress: async () => {
              try {
                await returnModuleSubmission(
                  item?.student?.id || '',
                  item?.course?.id || '',
                  item?.module?.id || '',
                  'student',
                );
                showToastMessage('success', 'Submission returned to student successfully');
                if (queryClient) {
                  queryClient.invalidateQueries({ queryKey: [`/${objectName}`] });
                }
                setRefreshKey(Date.now());
              } catch (error: unknown) {
                log.error('Return submission (student) error:', error);
                showToastMessage('error', `Failed to return submission: ${(error as Error).message}`);
              }
            },
          },
        ],
      });
    },
  },
  returnSubmissionToLecturer: {
    id: 'returnSubmissionToLecturer',
    label: 'Return to lecturer for re-evaluation',
    icon: Reply,
    position: 'row',
    rowStyle: 'menu',
    availCondition: (data) => {
      const item = data as SubmissionRecord;
      return item?.state === ModuleSubmissionState.graded;
    },
    handler: async ({ data, utils, objectName }: ActionHandlerProps) => {
      const {
        showDialog, showToastMessage, setRefreshKey, queryClient,
      } = utils;
      const item = data as SubmissionRecord;

      showDialog({
        title: 'Return Submission to Lecturer',
        message: 'Are you sure you want to return this submission to the lecturer?',
        buttons: [
          { text: 'Cancel', variant: 'outline', action: 'secondary' },
          {
            text: 'Return to Lecturer',
            action: 'primary',
            onPress: async () => {
              try {
                await returnModuleSubmission(
                  item?.student?.id || '',
                  item?.course?.id || '',
                  item?.module?.id || '',
                  'lecturer',
                );
                showToastMessage('success', 'Submission returned to lecturer successfully');
                if (queryClient) {
                  queryClient.invalidateQueries({ queryKey: [`/${objectName}`] });
                }
                setRefreshKey(Date.now());
              } catch (error: unknown) {
                log.error('Return submission (lecturer) error:', error);
                showToastMessage('error', `Failed to return submission: ${(error as Error).message}`);
              }
            },
          },
        ],
      });
    },
  },
  upgradeSubmissionToOral: {
    id: 'upgradeSubmissionToOral',
    label: 'Return to student and upgrade to oral exam',
    icon: GraduationCap,
    position: 'row',
    rowStyle: 'menu',
    availCondition: (data) => {
      const item = data as SubmissionRecord;
      return item?.examNextAction === 'upgrade_to_oral';
    },
    handler: async ({ data, utils, objectName }: ActionHandlerProps) => {
      const {
        showDialog, showToastMessage, setRefreshKey, queryClient, hideDialog,
      } = utils;
      const item = data as SubmissionRecord;
      showDialog({
        title: 'Upgrade to an oral exam',
        message: 'The written result stays on the student\'s record. The module moves to the newest published version that has an oral exam, and the student can register for that exam.',
        buttons: [
          { text: 'Cancel', variant: 'outline', action: 'secondary' },
          {
            text: 'Upgrade',
            action: 'primary',
            preventAutoClose: true,
            onPress: async () => {
              try {
                const response = await upgradeSubmissionToOralExam(
                  item?.student?.id || '',
                  item?.course?.id || '',
                  item?.module?.id || '',
                );
                const emailSent = (response?.data as { data?: { emailSent?: boolean } })?.data?.emailSent;
                if (emailSent === false) {
                  showToastMessage('warning', 'The module was upgraded, but the email to the student failed.');
                } else {
                  showToastMessage('success', 'Submission upgraded to the oral exam');
                }
                hideDialog();
                if (queryClient) {
                  queryClient.invalidateQueries({ queryKey: [`/${objectName}`] });
                }
                setRefreshKey(Date.now());
              } catch (error: unknown) {
                log.error('Upgrade to oral exam error:', error);
                showToastMessage('error', `Failed to upgrade: ${(error as Error).message}`);
              }
            },
          },
        ],
      });
    },
  },
  grantAdditionalAttempt: {
    id: 'grantAdditionalAttempt',
    label: 'Grant additional attempt',
    icon: Plus,
    position: 'row',
    rowStyle: 'menu',
    availCondition: (data) => {
      const item = data as SubmissionRecord;
      if (item?.examAttemptsAllowed == null) return false;
      return item.examNextAction !== 'passed' && item.examNextAction !== 'policy_off';
    },
    disabledCondition: (data) => {
      const item = data as SubmissionRecord;
      return item?.examNextAction !== 'exhausted';
    },
    handler: async ({ data, utils, objectName }: ActionHandlerProps) => {
      const {
        showDialog, showToastMessage, setRefreshKey, queryClient, hideDialog,
      } = utils;
      const item = data as SubmissionRecord;
      let noteDraft = '';
      showDialog({
        title: 'Grant another attempt',
        message: 'Adds one oral exam attempt. A note is required.',
        children: <GrantNoteField onNote={(value) => { noteDraft = value; }} />,
        buttons: [
          { text: 'Cancel', variant: 'outline', action: 'secondary' },
          {
            text: 'Grant another attempt',
            action: 'primary',
            preventAutoClose: true,
            onPress: async () => {
              const note = noteDraft.trim();
              if (!note) {
                showToastMessage('error', 'A note is required');
                return;
              }
              try {
                const response = await grantExamAttempt(
                  item?.student?.id || '',
                  item?.course?.id || '',
                  item?.module?.id || '',
                  { note, clientRequestId: requestId() },
                );
                const emailSent = (response?.data as { data?: { emailSent?: boolean } })?.data?.emailSent;
                if (emailSent === false) {
                  showToastMessage('warning', 'The extra attempt was granted, but the email to the student failed.');
                } else {
                  showToastMessage('success', 'Additional attempt granted');
                }
                hideDialog();
                if (queryClient) {
                  queryClient.invalidateQueries({ queryKey: [`/${objectName}`] });
                }
                setRefreshKey(Date.now());
              } catch (error: unknown) {
                log.error('Grant attempt error:', error);
                showToastMessage('error', `Failed to grant an attempt: ${(error as Error).message}`);
              }
            },
          },
        ],
      });
    },
  },
  deleteSubmission: {
    id: 'deleteSubmission',
    label: 'Delete',
    icon: Trash,
    position: 'row',
    rowStyle: 'menu',
    availCondition: submittedOrGraded,
    handler: async ({ data, utils, objectName }: ActionHandlerProps) => {
      const {
        showDialog, showToastMessage, setRefreshKey, queryClient,
      } = utils;
      const item = data as SubmissionRecord;

      showDialog({
        title: 'Delete Submission',
        message: 'Be careful: this deletes all student work in this module and cannot be recovered.',
        buttons: [
          { text: 'Cancel', variant: 'outline', action: 'secondary' },
          {
            text: 'Delete Submission',
            action: 'negative',
            onPress: async () => {
              try {
                await deleteModuleSubmission(
                  item?.student?.id || '',
                  item?.course?.id || '',
                  item?.module?.id || '',
                );
                showToastMessage('success', 'Submission deleted successfully');
                if (queryClient) {
                  queryClient.invalidateQueries({ queryKey: [`/${objectName}`] });
                }
                setRefreshKey(Date.now());
              } catch (error: unknown) {
                log.error('Delete submission error:', error);
                showToastMessage('error', `Failed to delete submission: ${(error as Error).message}`);
              }
            },
          },
        ],
      });
    },
  },
};
